// useStickyState.tsx

import { useState, useEffect, useRef, type Dispatch, type SetStateAction } from "react";

type SaveableData = string | number | object | boolean | undefined | null;

export default function useStickyState(
  defaultValue: string,
  key: string
): [string, Dispatch<SetStateAction<string>>];
export default function useStickyState<T extends SaveableData>(
  defaultValue: T,
  key: string
): [T, Dispatch<SetStateAction<T>>];
export default function useStickyState<T extends SaveableData>(
  defaultValue: T,
  key: string
): [T, Dispatch<SetStateAction<T>>] {
    const [value, setValue] = useState(() => readStoredValue(defaultValue, key));
    const skipSaveRef = useRef(false);

    useEffect(() => {
        skipSaveRef.current = true;
        setValue(readStoredValue(defaultValue, key));
    }, [defaultValue, key]);

    useEffect(() => {
        if (skipSaveRef.current) {
            skipSaveRef.current = false;
            return;
        }
        localStorage.setItem(key, JSON.stringify(value));
    }, [key, value]);

    return [value, setValue];
}

function readStoredValue<T extends SaveableData>(defaultValue: T, key: string): T {
    const v = localStorage.getItem(key);
    if (v === null) {
        return defaultValue;
    }
    try {
        return JSON.parse(v);
    } catch (e) {
        console.error("error parsing saved state from useStickyState");
        return defaultValue;
    }
}
